/**
 * v1.5.3 GastroGest — Cierre de día / corte de caja. El reporte que todo
 * restaurante espera al cerrar: venta del día (o turno) DESGLOSADA por método de
 * pago (efectivo / tarjeta / transferencia) + propinas + # órdenes + totales.
 * Estilo "resumen imprimible" (corte de caja). Se hidrata de
 * /pos/insights/cash-close (no estado local). Day-scoped: el corte es por día/turno.
 */
import { useCallback, useEffect, useState } from 'react';
import { Calculator, Printer, Banknote, CreditCard, ArrowLeftRight, Settings, Globe } from 'lucide-react';
import { api } from '../lib/api';
import { useFloorActing } from './FloorLock';
import { cashCloseTicket } from '../lib/escpos';
import { corteHtml } from '../printing/ticket';
import { printTicket, PrinterSettingsModal } from './printing';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v: any) => Number(v ?? 0).toLocaleString('es-MX');

/** Hoy en zona MX como YYYY-MM-DD (en-CA → formato ISO), para el <input type=date>. */
const mxToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

// PINK #2 PR C — crédito y débito separados; 'CARD' = legacy sin especificar (cobros viejos).
const METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', CARD_CREDIT: 'Tarjeta crédito', CARD_DEBIT: 'Tarjeta débito',
  CARD: 'Tarjeta (sin especificar)', TRANSFER: 'Transferencia', ONLINE: 'En línea',
};
const METHOD_ICON: Record<string, any> = {
  CASH: Banknote, CARD_CREDIT: CreditCard, CARD_DEBIT: CreditCard, CARD: CreditCard,
  TRANSFER: ArrowLeftRight, ONLINE: Globe,
};

interface MethodRow { method: string; sales: number; tips: number; orders: number; collected: number; tipsInCash?: number; }
interface UserRow { userId: string | null; name: string; sales: number; tips: number; orders: number; } // 🅒
interface OnlineBlock { sales: number; noShow: number; refunds: number; net: number; count: number; allBranches: boolean; }
// 🅐 pieza 5 — un ticket del día (solo lectura; el detalle se carga al desplegar).
export interface TicketRow {
  /** V220 — 'membership' = cobro de membresía sin orden: sin detalle; `label` lo describe. */
  kind?: 'order' | 'membership'; label?: string | null;
  orderId: string | null; paidAt: string | null; tableName: string | null; deliveryCode: string | null;
  openedByUserId: string | null; openedByName: string | null; registeredByName: string | null;
  method: string | null; total: number; tip: number;
}
// Detalle al desplegar (GET /pos/orders/:id — items con modificadores + totales).
interface TicketDetail {
  items: Array<{ id: string; description: string; qty: number; lineTotal: number; modifiers?: Array<{ name: string }>; notes?: string | null }>;
  subtotal: number; taxTotal: number; total: number;
}
interface CashClose {
  date: string;
  shift: { start: number | null; end: number | null } | null;
  byMethod: MethodRow[];
  byUser: UserRow[]; // 🅒 — ventas por mesero (quién abrió la orden)
  awaitingPaymentCount?: number; // 🅐 pieza 1 — mesas en "esperando pago" (aviso fuerte)
  tickets?: TicketRow[]; // 🅐 pieza 5 — tickets del día (respeta el filtro por mesero)
  /** V174 (PINK #3) — true = rol Mesero: el backend AUTO-ACOTÓ el corte a SUS tickets. */
  restricted?: boolean;
  totals: {
    subtotal: number; taxTotal: number; sales: number; tips: number;
    collected: number; orders: number; collectedIncludingOnline: number;
    /** §30 — diferencial de redondeo del cambio en efectivo absorbido en el periodo. */
    roundingDiff: number;
    /** §30 fast-follow — efectivo COBRADO (venta en efectivo, fila CASH). */
    cashCollected: number;
    /** §30 fast-follow — efectivo ESPERADO en el cajón = cashCollected − roundingDiff. */
    expectedCash: number;
    /** PINK #2 PR C — propina en efectivo del día (la que el mesero se lleva en mano). */
    cashTips?: number;
    /** V220 — cobros de membresía del día (ya incluidos en el desglose, el total y el efectivo). */
    memberships?: { count: number; collected: number };
  };
  // PR3 — reservas en línea (Epic Pagos): informativo, SEPARADO del arqueo físico.
  onlineReservations: OnlineBlock;
  includeOnlineInClose: boolean;
}

/** Etiqueta legible del día (en MX, sin desfase de zona: parseamos como local). */
const dayLabel = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
const shiftLabel = (s: CashClose['shift']) =>
  s && (s.start != null || s.end != null)
    ? `Turno ${s.start != null ? `${s.start}:00` : '—'} a ${s.end != null ? `${s.end}:59` : '—'}`
    : 'Día completo';

/** V220 — un cobro de membresía en la lista de tickets: sin orden, no se despliega. */
export function MembershipTicketLine({ t, hora }: { t: TicketRow; hora: string }) {
  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-left text-sm">
      <span className="w-12 shrink-0 tabular-nums text-slate-500">{hora}</span>
      <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{t.label ?? 'Membresía'}</span>
      <span className="hidden text-xs text-slate-500 sm:inline">Cobró: {t.registeredByName ?? '—'}</span>
      <span className="text-xs text-slate-400">{METHOD_LABEL[t.method ?? ''] ?? t.method ?? '—'}</span>
      <span className="w-20 shrink-0 text-right tabular-nums font-semibold text-slate-900">{mxn(t.total)}</span>
      <span className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-500">{t.tip > 0 ? `+${mxn(t.tip)}` : '—'}</span>
    </div>
  );
}

export function GastroGestCashClose({ sourceModule = 'gastro', me }: { sourceModule?: 'gastro' | 'boutique' | 'gym'; me?: any }) {
  // PINK #1 (P1) — sesión compartida de tablet: "Mis tickets" se acota por el
  // MESERO del PIN (no por el usuario compartido). TREN 1.2: el PIN ya lo pidió
  // la PANTALLA DE BLOQUEO — aquí se usa el acting token del contexto (si
  // venció, el lock reaparece solo). "Cambiar de mesero" = bloquear (re-PIN).
  const isFloor = !!me?.isFloor;
  const floorCtx = useFloorActing();
  // Strings estables como dependencias (un objeto derivado ciclaría el efecto).
  const actingToken = isFloor ? floorCtx.token() : null;
  const actingName = isFloor ? floorCtx.actingName() : null;
  const [date, setDate] = useState(mxToday());
  const [shiftStart, setShiftStart] = useState('');
  const [shiftEnd, setShiftEnd] = useState('');
  const [data, setData] = useState<CashClose | null>(null);
  const [loading, setLoading] = useState(true);
  const [printerOpen, setPrinterOpen] = useState(false);
  const [printMsg, setPrintMsg] = useState<string | null>(null);
  const brandName = me?.tenant?.branding?.brandName || me?.tenant?.name || 'FluidSol';
  const tenantId = me?.tenant?.id;
  // PR3 — el toggle "incluir en línea en el total" es del DUEÑO (financials:view);
  // el cajero solo LEE el corte. (Mismo criterio que hasPermission del dashboard.)
  const canManageClose = me?.role === 'SUPER_USER'
    || (Array.isArray(me?.permissions) && me.permissions.includes('financials:view'));
  const [savingToggle, setSavingToggle] = useState(false);
  // 🅐 pieza 5 — filtro por mesero ('' = todos). Con uno, los totales de arriba se
  // recalculan a ese mesero (el backend filtra byMethod/totals/tickets por opened_by).
  const [waiterFilter, setWaiterFilter] = useState('');
  // Detalle desplegado por ticket (orderId → detalle cargado; null = cargando).
  const [openTicket, setOpenTicket] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, TicketDetail>>({});

  const load = useCallback(() => {
    // PINK #1 (P1): en sesión de piso no se carga nada sin PIN verificado.
    if (isFloor && !actingToken) { setLoading(false); setData(null); return; }
    setLoading(true);
    const qs = new URLSearchParams();
    if (date) qs.set('date', date);
    if (shiftStart !== '') qs.set('shiftStart', shiftStart);
    if (shiftEnd !== '') qs.set('shiftEnd', shiftEnd);
    qs.set('sourceModule', sourceModule);
    if (waiterFilter) qs.set('openedBy', waiterFilter);
    // El acting viaja en el header X-Floor-Acting (api) — nada en el query.
    api(`/pos/insights/cash-close?${qs.toString()}`)
      .then((r) => setData(r as CashClose))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, shiftStart, shiftEnd, sourceModule, waiterFilter, isFloor, actingToken]);
  useEffect(() => { load(); }, [load]);

  // 🅐 pieza 5 — despliega/colapsa un ticket; el detalle se carga UNA vez y se cachea.
  const toggleTicket = async (orderId: string) => {
    if (openTicket === orderId) { setOpenTicket(null); return; }
    setOpenTicket(orderId);
    if (!details[orderId]) {
      try {
        const d = (await api(`/pos/orders/${orderId}`)) as TicketDetail;
        setDetails((prev) => ({ ...prev, [orderId]: d }));
      } catch { /* transitorio; el renglón muestra "cargando" hasta reintentar */ }
    }
  };

  // PR3 — el dueño incluye/separa las reservas en línea del total del día. NUNCA
  // toca el arqueo físico; solo cambia qué "total" se resalta.
  const toggleIncludeOnline = async (next: boolean) => {
    if (!canManageClose || savingToggle) return;
    setSavingToggle(true);
    try {
      await api('/pos/insights/cash-close/settings', { method: 'PUT', body: { includeOnlineInClose: next } });
      setData((d) => (d ? { ...d, includeOnlineInClose: next } : d));
    } catch { /* la API valida; si falla, se queda como estaba */ }
    finally { setSavingToggle(false); }
  };

  // V146 — imprime el corte por el ROL "caja" (qz/red silencioso o navegador 58/80mm).
  const print = async () => {
    if (!data) return;
    setPrintMsg(null);
    const cd = {
      brandName, dateLabel: dayLabel(data.date), shiftLabel: shiftLabel(data.shift),
      byMethod: data.byMethod, totals: data.totals,
    };
    const res = await printTicket({
      tenantId, branchId: null, kind: 'corte',
      escpos: (w) => cashCloseTicket({ ...cd, cols: w === 58 ? 32 : 48 }),
      html: (w) => corteHtml(cd, w),
    });
    setPrintMsg(res.usedFallback || res.method === 'browser'
      ? `⚠ No imprimió en la impresora de caja — se abrió la vista del navegador. ${res.error ?? ''}`
      : 'Corte enviado a la impresora.');
  };

  // TREN 1.2 — sin acting vivo, la PANTALLA DE BLOQUEO del provider ya cubre la
  // app; este estado solo existe el instante del vencimiento.
  if (isFloor && !actingToken) {
    return <p className="text-sm text-slate-400">Pantalla bloqueada — teclea tu PIN.</p>;
  }

  return (
    <div className="space-y-5">
      {/* PINK #1 — a quién pertenece la vista (PIN del desbloqueo) + cambio de mesero. */}
      {isFloor && actingToken && (
        <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span>Tickets de <strong>{actingName}</strong> (por PIN)</span>
          <button className="font-medium underline decoration-dotted hover:text-slate-900"
            onClick={() => floorCtx.lock()}>Cambiar de mesero</button>
        </div>
      )}
      {/* 🅐 pieza 1 — AVISO FUERTE: mesas en "esperando pago" (pre-cuenta impresa, sin cobrar).
          Son ventas a medio cobrar; no deben quedar sueltas al cerrar el día. */}
      {!!data?.awaitingPaymentCount && data.awaitingPaymentCount > 0 && (
        <div className="flex items-center gap-3 rounded-2xl border-2 border-violet-300 bg-violet-50 px-5 py-4">
          <span className="text-2xl">⏳</span>
          <div>
            <p className="font-serif text-lg text-violet-900">
              {data.awaitingPaymentCount} {data.awaitingPaymentCount === 1 ? 'mesa está' : 'mesas están'} en «esperando pago»
            </p>
            <p className="text-sm text-violet-700">Imprimiste su pre-cuenta pero no se ha cobrado. Cóbralas (o cancela la pre-cuenta) antes de cerrar el día — es una venta a medio cobrar.</p>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {/* V174 (PINK #3) — rol Mesero: NO ve el cierre; ve SUS tickets del día. */}
          {data?.restricted ? (
            <>
              <h2 className="font-serif text-2xl tracking-tight text-slate-900">Mis tickets del día</h2>
              <p className="text-sm text-slate-500">Tus ventas, propinas y tickets. El cierre de día completo lo hace el encargado.</p>
            </>
          ) : (
            <>
              <h2 className="font-serif text-2xl tracking-tight text-slate-900">Cierre de día · Corte de caja</h2>
              <p className="text-sm text-slate-500">Venta del día por método de pago, propinas y total cobrado.</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Día
            <input type="date" className="input !w-auto !py-1.5 text-sm" value={date} max={mxToday()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="flex flex-col text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Turno de
            <input className="input !w-16 !py-1.5 text-sm" type="number" min={0} max={23} placeholder="0" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} />
          </label>
          <label className="flex flex-col text-[11px] font-medium uppercase tracking-wide text-slate-400">
            a
            <input className="input !w-16 !py-1.5 text-sm" type="number" min={0} max={23} placeholder="23" value={shiftEnd} onChange={(e) => setShiftEnd(e.target.value)} />
          </label>
          {(shiftStart !== '' || shiftEnd !== '') && (
            <button className="pb-2 text-xs font-medium text-slate-500 hover:text-slate-900" onClick={() => { setShiftStart(''); setShiftEnd(''); }}>Limpiar turno</button>
          )}
          <button onClick={() => setPrinterOpen(true)} title="Impresora de esta estación"
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50">
            <Settings className="h-4 w-4" />
          </button>
          <button onClick={print} disabled={!data || loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] shadow-sm transition hover:bg-[var(--brand-primary-hover)] disabled:opacity-40">
            <Printer className="h-4 w-4" /> Imprimir ticket
          </button>
        </div>
      </div>

      {printMsg && <p className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">{printMsg}</p>}
      {printerOpen && <PrinterSettingsModal tenantId={tenantId} onClose={() => setPrinterOpen(false)} />}

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !data ? (
        <p className="text-sm text-slate-400">No se pudo cargar el corte.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Total cobrado" value={mxn(data.totals.collected)} hint={`${shiftLabel(data.shift)} · ${dayLabel(data.date)}`} accent />
            <Tile label="Ventas (c/IVA)" value={mxn(data.totals.sales)} hint={`Subtotal ${mxn(data.totals.subtotal)} · IVA ${mxn(data.totals.taxTotal)}`} />
            <Tile label="Propinas" value={mxn(data.totals.tips)} />
            <Tile label="Órdenes" value={int(data.totals.orders)} />
          </div>
          {/* §30 fast-follow — ARQUEO de la caja de efectivo. Solo se muestra cuando
              hubo diferencial de redondeo: sin él, "Efectivo esperado" == la fila
              "Efectivo · Cobrado" del desglose → sería ruido duplicado. El "Total
              cobrado" (venta) NO se toca; esto responde otra pregunta: cuánto debe
              haber físicamente en el cajón. */}
          {data.totals.roundingDiff > 0 && (
            <section className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">
                  <Banknote className="h-4 w-4 text-amber-600" /> Arqueo de efectivo
                </h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                  Redondeo del cambio a favor del cliente
                </span>
              </div>
              <div className="mx-auto max-w-sm space-y-1.5 text-sm">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Efectivo cobrado</span>
                  <span className="tabular-nums">{mxn(data.totals.cashCollected)}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>Diferencial de redondeo</span>
                  <span className="tabular-nums">− {mxn(data.totals.roundingDiff)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-amber-200 pt-2 font-semibold text-slate-900">
                  <span>Efectivo esperado en caja</span>
                  <span className="tabular-nums text-lg">{mxn(data.totals.expectedCash)}</span>
                </div>
              </div>
              <p className="mt-3 text-center text-xs text-slate-500">
                Lo que debe haber en el cajón. La venta (Total cobrado {mxn(data.totals.collected)}) no cambia — el diferencial lo absorbe el negocio.
              </p>
            </section>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-900">
              <Calculator className="h-4 w-4 text-slate-500" /> Desglose por método de pago
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Método</th>
                    <th className="px-3 py-2 text-right">Ventas</th>
                    <th className="px-3 py-2 text-right">Propinas</th>
                    <th className="px-3 py-2 text-right">Órdenes</th>
                    <th className="px-3 py-2 text-right">Cobrado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.byMethod.map((r) => {
                    const Icon = METHOD_ICON[r.method];
                    const muted = r.orders === 0 && r.collected === 0;
                    return (
                      <tr key={r.method} className={muted ? 'text-slate-300' : ''}>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center gap-2 font-medium ${muted ? 'text-slate-300' : 'text-slate-800'}`}>
                            {Icon && <Icon className="h-4 w-4" />} {METHOD_LABEL[r.method] ?? r.method}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{mxn(r.sales)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{mxn(r.tips)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{int(r.orders)}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${muted ? '' : 'text-slate-900'}`}>{mxn(r.collected)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
                    <td className="px-3 py-2.5">Total</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{mxn(data.totals.sales)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{mxn(data.totals.tips)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{int(data.totals.orders)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{mxn(data.totals.collected)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {(data.totals.memberships?.count ?? 0) > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                Incluye {int(data.totals.memberships!.count)} cobro(s) de membresía por {mxn(data.totals.memberships!.collected)} (alta, renovación o cambio de plan); los de efectivo ya están en el efectivo esperado.
              </p>
            )}
            {data.totals.orders === 0 && (data.totals.memberships?.count ?? 0) === 0 && (
              <p className="mt-3 text-sm text-slate-400">Sin ventas cerradas en este día/turno.</p>
            )}
          </section>

          {/* PINK #2 PR C — PROPINAS: total del día, y aparte la PROPINA EN EFECTIVO (la que
              el mesero se lleva en mano, aunque el pago haya sido con tarjeta). */}
          {data.totals.tips > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-900">
                <Banknote className="h-4 w-4 text-slate-500" /> Propinas
              </h3>
              <div className="mx-auto max-w-sm space-y-1.5 text-sm">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Propinas del día</span>
                  <span className="tabular-nums">{mxn(data.totals.tips)}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>En efectivo (la recibe el mesero)</span>
                  <span className="tabular-nums font-semibold text-emerald-700">{mxn(data.totals.cashTips ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-slate-600">
                  <span>Por terminal / cuenta</span>
                  <span className="tabular-nums">{mxn(data.totals.tips - (data.totals.cashTips ?? 0))}</span>
                </div>
              </div>
              {/* Desglose por método (solo métodos con propina). */}
              {data.byMethod.some((r) => r.tips > 0) && (
                <div className="mx-auto mt-3 max-w-sm space-y-1 border-t border-slate-100 pt-3 text-[13px]">
                  {data.byMethod.filter((r) => r.tips > 0).map((r) => (
                    <div key={r.method} className="flex items-center justify-between text-slate-500">
                      <span>{METHOD_LABEL[r.method] ?? r.method}{(r.tipsInCash ?? 0) > 0 ? ` · ${mxn(r.tipsInCash ?? 0)} en efectivo` : ''}</span>
                      <span className="tabular-nums">{mxn(r.tips)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* 🅒 — Ventas por MESERO (quién ABRIÓ la orden). Sin desglose por método
              (eso depende de 🅐). "Cada mesero sabe cuánto vendió." */}
          {data.byUser && data.byUser.length > 0 && (
            <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
              <h3 className="mb-3 font-serif text-lg text-slate-900">Ventas por mesero</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-[12px] uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2">Mesero</th>
                      <th className="px-3 py-2 text-right">Ventas</th>
                      <th className="px-3 py-2 text-right">Propinas</th>
                      <th className="px-3 py-2 text-right">Órdenes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.byUser.map((r) => (
                      <tr key={r.userId ?? 'none'} className={waiterFilter && waiterFilter === r.userId ? 'bg-slate-50' : ''}>
                        {/* Ajuste smoke — el NOMBRE filtra al clicarlo (mismo efecto que el
                            selector de Tickets del día); clic de nuevo = quitar filtro. */}
                        <td className="px-3 py-2.5 font-medium text-slate-800">
                          {r.userId ? (
                            <button type="button"
                              onClick={() => { setOpenTicket(null); setWaiterFilter(waiterFilter === r.userId ? '' : r.userId!); }}
                              title={waiterFilter === r.userId ? 'Quitar filtro' : `Filtrar el corte a ${r.name}`}
                              className="underline decoration-dotted underline-offset-2 hover:text-slate-950">
                              {r.name}{waiterFilter === r.userId ? ' ✕' : ''}
                            </button>
                          ) : r.name}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{mxn(r.sales)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{mxn(r.tips)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{int(r.orders)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">La venta se atribuye a quién <strong>abrió</strong> la orden; cobrar puede hacerlo otra persona.</p>
            </section>
          )}

          {/* 🅐 pieza 5 — TICKETS DEL DÍA (solo lectura). Cada renglón se despliega con el
              detalle (renglones + modificadores + totales + quién cobró). Filtro por mesero:
              con uno seleccionado, los totales de ARRIBA se recalculan a ese mesero. */}
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-lg text-slate-900">{data.restricted ? 'Mis tickets' : 'Tickets del día'}</h3>
              {/* V174 — el Mesero no filtra por otros: el backend ya acotó a sus tickets. */}
              {!data.restricted && (
                <label className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Mesero
                  <select className="input !w-auto !py-1.5 text-sm normal-case" value={waiterFilter}
                          onChange={(e) => { setOpenTicket(null); setWaiterFilter(e.target.value); }}>
                    <option value="">Todos</option>
                    {data.byUser.filter((u) => u.userId).map((u) => (
                      <option key={u.userId!} value={u.userId!}>{u.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {waiterFilter && (
              <p className="mb-2 rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
                Totales de arriba recalculados al mesero seleccionado (las reservas en línea no se filtran).
              </p>
            )}
            {(data.tickets ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">Sin tickets en este día/turno.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {(data.tickets ?? []).map((t, i) => {
                  const hora = t.paidAt
                    ? new Date(t.paidAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' })
                    : '—';
                  // V220 — cobro de membresía: sin orden ni detalle que desplegar.
                  if (t.kind === 'membership' || !t.orderId) return <MembershipTicketLine key={`m-${i}`} t={t} hora={hora} />;
                  const orderId = t.orderId;
                  const open = openTicket === orderId;
                  const det = details[orderId];
                  const lugar = t.tableName ?? (t.deliveryCode ? `Recoger #${t.deliveryCode}` : 'Venta rápida');
                  return (
                    <div key={orderId}>
                      <button onClick={() => toggleTicket(orderId)}
                        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-left text-sm hover:bg-slate-50">
                        <span className="w-12 shrink-0 tabular-nums text-slate-500">{hora}</span>
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{lugar}</span>
                        <span className="hidden text-xs text-slate-500 sm:inline">{t.openedByName ?? 'Sin mesero'}</span>
                        <span className="text-xs text-slate-400">{METHOD_LABEL[t.method ?? ''] ?? t.method ?? '—'}</span>
                        <span className="w-20 shrink-0 text-right tabular-nums font-semibold text-slate-900">{mxn(t.total)}</span>
                        <span className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-500">{t.tip > 0 ? `+${mxn(t.tip)}` : '—'}</span>
                        <span className="text-slate-300">{open ? '▾' : '▸'}</span>
                      </button>
                      {open && (
                        <div className="mb-2 rounded-xl bg-slate-50 px-4 py-3 text-sm">
                          {!det ? <p className="text-xs text-slate-400">Cargando detalle…</p> : (
                            <>
                              <ul className="mb-2 divide-y divide-slate-200/60">
                                {det.items.map((it) => (
                                  <li key={it.id} className="py-1">
                                    <div className="flex justify-between">
                                      <span className="text-slate-700"><span className="tabular-nums text-slate-400">{Number(it.qty)}×</span> {it.description}</span>
                                      <span className="tabular-nums text-slate-800">{mxn(it.lineTotal)}</span>
                                    </div>
                                    {!!it.modifiers?.length && (
                                      <p className="pl-5 text-[11px] text-slate-500">{it.modifiers.map((m) => m.name).join(' · ')}</p>
                                    )}
                                    {it.notes && <p className="pl-5 text-[11px] italic text-slate-400">{it.notes}</p>}
                                  </li>
                                ))}
                              </ul>
                              <div className="grid gap-x-6 gap-y-0.5 text-xs text-slate-600 sm:grid-cols-2">
                                <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{mxn(det.subtotal)}</span></div>
                                <div className="flex justify-between"><span>IVA</span><span className="tabular-nums">{mxn(det.taxTotal)}</span></div>
                                <div className="flex justify-between font-semibold text-slate-800"><span>Total</span><span className="tabular-nums">{mxn(det.total)}</span></div>
                                <div className="flex justify-between"><span>Propina</span><span className="tabular-nums">{mxn(t.tip)}</span></div>
                              </div>
                              <p className="mt-2 text-[11px] text-slate-400">
                                Abrió: <strong>{t.openedByName ?? 'Sin mesero'}</strong> · Cobró: <strong>{t.registeredByName ?? '—'}</strong>
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-[11px] text-slate-400">Solo lectura — desde aquí no se reabre ni se edita ningún ticket.</p>
          </section>

          {/* PR3 — reservas EN LÍNEA (Epic Pagos): informativo, SEPARADO del arqueo
              físico de arriba. El cajero cuenta el cajón contra "Cobrado (caja)",
              nunca contra este bloque. Solo se muestra si hubo actividad en línea. */}
          {(data.onlineReservations.count > 0 || data.onlineReservations.net !== 0
            || data.onlineReservations.sales !== 0 || data.onlineReservations.refunds !== 0) && (
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">
                  <Globe className="h-4 w-4 text-emerald-600" /> Reservas en línea
                </h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  No afecta el arqueo de caja física{data.onlineReservations.allBranches ? ' · todas las sucursales' : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <MiniStat label="Cobrado" value={mxn(data.onlineReservations.sales)} />
                <MiniStat label="Inasistencia" value={mxn(data.onlineReservations.noShow)} />
                <MiniStat label="Reembolsos" value={`− ${mxn(data.onlineReservations.refunds)}`} />
                <MiniStat label={`Neto · ${int(data.onlineReservations.count)} reserva(s)`} value={mxn(data.onlineReservations.net)} strong />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-emerald-200 pt-3">
                <p className="text-sm text-slate-700">
                  Total del día {data.includeOnlineInClose ? 'incluyendo' : 'sin'} reservas en línea:{' '}
                  <b className="tabular-nums">
                    {mxn(data.includeOnlineInClose ? data.totals.collectedIncludingOnline : data.totals.collected)}
                  </b>
                  <span className="ml-2 text-xs text-slate-400">caja física {mxn(data.totals.collected)}</span>
                </p>
                {canManageClose && (
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                    <input type="checkbox" checked={data.includeOnlineInClose} disabled={savingToggle}
                      onChange={(e) => toggleIncludeOnline(e.target.checked)} />
                    Incluir en el total del día
                  </label>
                )}
              </div>
            </section>
          )}
          {/* §31.4 (V130) — recordatorio de la mecánica de sugeridos. Va en el cierre
              de día (y en Configuración), NO como aviso permanente en la pantalla de
              cobro (un aviso fijo se vuelve invisible en dos días). */}
          <p className="mt-2 text-center text-[11px] text-slate-400">
            Tip de upsell: marca postres/bebidas como <b>Sugeridos</b> en Productos y aparecerán en la franja del punto de venta (máx 4).
          </p>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`tabular-nums ${strong ? 'font-serif text-lg text-slate-900' : 'text-sm font-semibold text-slate-800'}`}>{value}</p>
    </div>
  );
}

function Tile({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className={`rounded-2xl border px-5 py-3 ${accent ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white'}`}>
      <p className={`text-[11px] uppercase tracking-wide ${accent ? 'text-white/60' : 'text-slate-400'}`}>{label}</p>
      <p className={`font-serif text-2xl tabular-nums ${accent ? 'text-white' : 'text-slate-900'}`}>{value}</p>
      {hint && <p className={`text-[11px] ${accent ? 'text-white/60' : 'text-slate-400'}`}>{hint}</p>}
    </div>
  );
}
